import React, { useState } from 'react';
import { Trophy, Users, Calendar, ChevronRight, Clock, Ban, Trash2, Star } from 'lucide-react';
import { Challenge } from '../../types/challenges';
import { auth } from '../../lib/firebase';
import { CloseConfirmationModal } from './CloseConfirmationModal';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';

interface ChallengeCardProps {
  challenge: Challenge;
  onJoin: (challengeId: string) => Promise<void>;
  onClose?: (challengeId: string) => Promise<void>;
  onDelete?: (challengeId: string) => Promise<void>;
  userParticipating: boolean;
  onClick: () => void;
}

export function ChallengeCard({ 
  challenge, 
  onJoin, 
  onClose,
  onDelete,
  userParticipating, 
  onClick 
}: ChallengeCardProps) {
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const isAdmin = auth.currentUser?.email === 'breadflowbybadabec@gmail.com';

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-100 text-green-800';
      case 'intermediate':
        return 'bg-amber-100 text-amber-800';
      case 'advanced':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const daysLeft = Math.ceil(
    (new Date(challenge.endDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  );

  const isInGracePeriod = !challenge.isActive && challenge.gracePeriodEnds && new Date(challenge.gracePeriodEnds) > new Date();
  const gracePeriodDaysLeft = isInGracePeriod ? 
    Math.ceil((new Date(challenge.gracePeriodEnds!).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 0;

  const userProgress = userParticipating && auth.currentUser
    ? challenge.participants.find(p => p.userId === auth.currentUser?.uid)?.progress || 0
    : 0;

  const isCompleted = userParticipating && auth.currentUser
    ? challenge.participants.find(p => p.userId === auth.currentUser?.uid)?.completed || false
    : false;

  const userResult = userParticipating && auth.currentUser
    ? challenge.participants.find(p => p.userId === auth.currentUser?.uid)?.result
    : null;

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const handleJoinClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onJoin(challenge.id);
  };

  const handleCloseClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowCloseConfirm(true);
  };

  const handleDeleteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };

  const handleConfirmClose = async () => {
    if (onClose) {
      await onClose(challenge.id);
    }
    setShowCloseConfirm(false);
  };

  const handleConfirmDelete = async () => {
    if (onDelete) {
      await onDelete(challenge.id);
    }
    setShowDeleteConfirm(false);
  };

  if (isCompleted && userResult) {
    const completedAt = challenge.participants.find(p => 
      p.userId === auth.currentUser?.uid
    )?.completedAt;

    return (
      <div
        onClick={onClick}
        className="bg-gradient-to-br from-[#636363] to-[#D8D8D8] overflow-hidden shadow-lg h-[200px] transition-shadow duration-200 cursor-pointer hover:border hover:border-amber-400 hover:shadow-xl"
      >
        <div className="flex flex-col sm:flex-row h-full">
          {/* Left side - Challenge info */}
          <div className="p-4 flex flex-col justify-between flex-grow sm:w-1/3 bg-opacity-90 shadow-lg">
            <div>
              <h3 className="text-2xl text-white font-brand font-light italic mb-1">{challenge.title}</h3>
              <div className="space-y-2">
                <div className="text-xs text-white">
                  <span>
                    Completed {completedAt ? formatDate(completedAt) : 'Completion date not available'}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 bg-gradient-to-br from-[#535353] to-[#7A7979] text-white px-3 py-2 rounded-lg space-y-1">
                  <span className="flex items-center gap-1 text-sm text-white">
                    <Users className="w-4 h-4" />
                    {challenge.participants.length} bakers
                  </span>
                  <span className="px-3 py-1 rounded-full text-sm font-medium">
                    {challenge.difficulty.charAt(0).toUpperCase() + challenge.difficulty.slice(1)}
                  </span>
                </div>
              </div>
            </div>

            {/* Challenge completed tag */}
            <div className="bg-gradient-to-r from-blue-500/90 to-blue-600/80 text-white font-brand font-light italic text-center py-2 px-4 rounded-lg mb-2">
              See Results
            </div>
          </div>

          {/* Right side - Result Image */}
          <div className="hidden sm:block w-full sm:w-2/3 h-full relative flex-shrink-0">
            <img
              src={userResult.imageUrl}
              alt={`Result for ${challenge.title}`}
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-y-0 left-0 w-[5px] bg-gradient-to-r from-black/30 to-transparent pointer-events-none bg-opacity-50"></div>
            <div className="absolute inset-y-0 left-0 w-[30px] bg-gradient-to-r from-black/30 to-transparent pointer-events-none bg-opacity-50"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        onClick={onClick}
        className="bg-gradient-to-br from-[#636363] to-[#D8D8D8] overflow-hidden shadow-lg h-[650px] sm:h-[500px] transition-shadow duration-200 cursor-pointer hover:border hover:border-amber-400 hover:shadow-xl"
      >
        <div className="flex flex-col sm:flex-row h-full">
          {/* Left side - Challenge info */}
          <div className="p-3 sm:p-4 flex flex-col justify-between sm:justify-center h-[300px] sm:h-auto sm:flex-grow sm:w-1/2 bg-opacity-90 shadow-lg">
            {/* Top content */}
            <div className="flex-1 sm:flex-none flex flex-col items-center justify-center">
              <div className="flex flex-col items-center gap-1 sm:gap-2 mb-2 sm:mb-4">
                <Trophy
                  className={`w-12 h-12 sm:w-16 sm:h-16 ${
                    challenge.difficulty === 'beginner' ? 'text-amber-400' :
                    challenge.difficulty === 'intermediate' ? 'text-amber-600' :
                    'text-red-600'
                  }`}
                  strokeWidth={1}
                />
                <h3 className="text-2xl sm:text-3xl text-white font-brand font-light italic text-center mt-2 sm:mt-4">
                  {challenge.title}
                </h3>
              </div>
              
              <div className="space-y-2">
                <div>
                  <div className="flex items-center justify-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(challenge.difficulty)}`}>
                      {challenge.difficulty.charAt(0).toUpperCase() + challenge.difficulty.slice(1)}
                    </span>
                    <span className="flex items-center gap-1 text-sm text-white">
                      <Users className="w-4 h-4" />
                      {challenge.participants.length} bakers
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2 mt-2 sm:mt-4 mb-2 sm:mb-4 py-2 sm:py-4">
                <div className="flex items-center gap-1 text-sm text-white">
                  {!isCompleted && (
                    isInGracePeriod ? (
                      <>
                        <Clock className="w-4 h-4 text-amber-400" />
                        <span className="text-amber-400">{gracePeriodDaysLeft} days to complete</span>
                      </>
                    ) : (
                      <>
                        <Calendar className="w-4 h-4" />
                        <span>{daysLeft} days left</span>
                      </>
                    )
                  )}
                </div>

                {/* Join button or "You joined" label */}
                {!isAdmin && !isCompleted && challenge.isActive && (
                  userParticipating ? (
                    <div className="px-4 py-2 bg-white/10 text-white text-sm rounded-lg italic">
                      Congratulations, you have joined this challenge!
                    </div>
                  ) : (
                    <button
                      onClick={handleJoinClick}
                      className="flex items-center gap-1 px-4 py-2 bg-gradient-to-r from-amber-500/90 to-amber-600/90 hover:from-amber-400 hover:to-amber-600 text-white rounded-lg text-sm"
                    >
                      Join Challenge
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  )
                )}
              </div>
            </div>

            {/* Bottom content - Admin buttons and More button */}
            <div className="sm:mt-8">
              <div className="flex justify-between items-center"> 
                {/* Admin buttons */}
                <div className="flex gap-2">
                  {isAdmin && challenge.isActive && (
                    <>
                      <button
                        onClick={handleCloseClick}
                        className="flex items-center gap-1 px-3 py-1.5 bg-amber-500 text-white rounded-lg hover:bg-amber-500/30 transition-colors"
                      >
                        <Ban className="w-4 h-4" />
                        Close
                      </button>
                      <button
                        onClick={handleDeleteClick}
                        className="flex items-center gap-1 px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-500/30 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </>
                  )}
                </div>

                {/* More button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick();
                  }}
                  className="px-3 py-1 bg-white/20 sm:bg-transparent hover:bg-white/30 sm:hover:bg-white/5 text-white rounded-xl text-sm font-medium transition-colors flex items-center gap-1"
                >
                  MORE
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Right side - Challenge Image */}
          <div className="w-full sm:w-1/2 flex-1 sm:flex-none h-auto sm:h-full relative">
            <img
              src={challenge.imageUrl}
              alt={challenge.title}
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-y-0 left-0 w-[5px] bg-gradient-to-r from-black/30 to-transparent pointer-events-none bg-opacity-50"></div>
            <div className="absolute inset-y-0 left-0 w-[30px] bg-gradient-to-r from-black/30 to-transparent pointer-events-none bg-opacity-50"></div>
          </div>
        </div>
      </div>

      <CloseConfirmationModal
        isOpen={showCloseConfirm}
        onClose={() => setShowCloseConfirm(false)}
        onConfirm={handleConfirmClose}
        challengeTitle={challenge.title}
      />

      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        challengeTitle={challenge.title}
        participantsCount={challenge.participants.length}
      />
    </>
  );
}